import React from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles'; // Import SxProps and Theme for sx prop typing

interface PlayerRaceIconProps {
  /**
   * The identifier for the player's race (e.g., 'HUMAN', 'ORC', 'DEVIL').
   * Used to construct the image path. Case-insensitive for path generation.
   */
  raceId: string;
  /**
   * The desired width and height of the icon. Accepts CSS units (e.g., '24px', '2vmin') or numbers (treated as px).
   */
  width: string | number;
  /**
   * The desired CSS color for the icon (e.g., 'white', '#000000', 'currentColor').
   */
  color?: string;
  /**
   * Optional MUI sx prop for additional styling overrides or additions.
   */
  sx?: SxProps<Theme>;
}

/**
 * Renders a player's race icon using a PNG mask, allowing dynamic coloring.
 */
export const PlayerRaceIcon: React.FC<PlayerRaceIconProps> = ({
  raceId,
  width,
  color,
  sx = {}, // Default sx to an empty object
}) => {
  // Construct the path to the icon based on the raceId
  // Ensure lowercase for consistent file naming convention
  const iconPath = `/images/races/${raceId.toLowerCase()}.png`;

  return (
    <Box
      component="span" // Render as a span for better inline integration with text
      sx={{
        // Base styles
        display: 'inline-block', // Crucial for applying width/height to an inline element
        verticalAlign: 'middle', // Align nicely with adjacent text
        width: width,
        height: width, // Assume icon is square

        // Masking styles
        bgcolor: color ?? 'black', // This sets the visible color of the icon's shape
        maskImage: `url(${iconPath})`,
        maskSize: 'contain', // Scale the mask to fit within the element
        maskRepeat: 'no-repeat',
        maskPosition: 'center',

        // Vendor prefix for maximum compatibility (Safari, older Chrome/Edge)
        WebkitMaskImage: `url(${iconPath})`,
        WebkitMaskSize: 'contain',
        WebkitMaskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',

        // Merge any additional sx props passed in
        ...sx,
      }}
    // Consider adding accessibility attributes if the icon stands alone
    // role="img"
    // aria-label={`${raceId} icon`}
    />
  );
};

export default PlayerRaceIcon;